import { Directive, ElementRef, NgZone, OnDestroy, OnInit } from '@angular/core';
import { MIRRORED_STYLE_PROPS } from './find-replace.directive';

/** Space kept between the caret line and the edge of what is shown. */
const MARGIN = 24;

/**
 * How far to scroll so the caret line sits inside the visible area, with a margin: positive to scroll down, negative
 * up, 0 when it is already in view.
 */
export function revealDelta(caret: { top: number; bottom: number }, view: { top: number; bottom: number }, margin: number): number {
  if (caret.bottom + margin > view.bottom) return caret.bottom + margin - view.bottom;
  if (caret.top - margin < view.top) return caret.top - margin - view.top;
  return 0;
}

/**
 * Keeps the caret of a growing textarea on screen while typing. An auto-sized note never scrolls itself; the panel
 * around it must, and mobile WebKit does not scroll that panel to the caret, nor account for the keyboard covering the
 * bottom of it, so a long note was typed blind. After each keystroke, and when the keyboard changes the visible area,
 * the caret's line is measured (with a hidden copy of the text, wrapped the same way) and the nearest scrolling
 * ancestors are scrolled just enough to show it.
 *
 * It applies to every `cdkTextareaAutosize` textarea: those are the long notes.
 */
@Directive({ selector: 'textarea[cdkTextareaAutosize]', standalone: true })
export class KeepCaretVisibleDirective implements OnInit, OnDestroy {
  private frame = 0;
  private readonly schedule = (): void => {
    cancelAnimationFrame(this.frame);
    this.frame = requestAnimationFrame(() => this.reveal());
  };
  private readonly onViewportResize = (): void => {
    if (document.activeElement === this.textarea) this.schedule();
  };

  constructor(private readonly ref: ElementRef<HTMLTextAreaElement>, private readonly zone: NgZone) {}

  private get textarea(): HTMLTextAreaElement {
    return this.ref.nativeElement;
  }

  ngOnInit(): void {
    this.zone.runOutsideAngular(() => {
      this.textarea.addEventListener('input', this.schedule);
      this.textarea.addEventListener('keyup', this.schedule);
      this.textarea.addEventListener('focus', this.schedule);
      window.visualViewport?.addEventListener('resize', this.onViewportResize);
    });
  }

  ngOnDestroy(): void {
    cancelAnimationFrame(this.frame);
    this.textarea.removeEventListener('input', this.schedule);
    this.textarea.removeEventListener('keyup', this.schedule);
    this.textarea.removeEventListener('focus', this.schedule);
    window.visualViewport?.removeEventListener('resize', this.onViewportResize);
  }

  private reveal(): void {
    const textarea = this.textarea;
    if (document.activeElement !== textarea || !textarea.clientWidth) return;
    const caret = this.caretLine();
    const scrollers = scrollingAncestors(textarea);
    let delta = revealDelta(caret, visibleArea(scrollers), MARGIN);
    for (const el of scrollers) {
      if (!delta) return;
      const before = el.scrollTop;
      el.scrollTop = before + delta;
      delta -= el.scrollTop - before;
    }
    if (delta) window.scrollBy(0, delta);
  }

  /** The caret's line, in viewport coordinates. */
  private caretLine(): { top: number; bottom: number } {
    const textarea = this.textarea;
    const styles = getComputedStyle(textarea);
    const mirror = document.createElement('div');
    MIRRORED_STYLE_PROPS.forEach((prop) => mirror.style.setProperty(prop, styles.getPropertyValue(prop)));
    Object.assign(mirror.style, {
      position: 'fixed', top: '0', left: '0', visibility: 'hidden', boxSizing: 'border-box',
      width: `${textarea.clientWidth}px`, whiteSpace: 'pre-wrap', overflowWrap: 'break-word', wordWrap: 'break-word',
    });
    mirror.textContent = textarea.value.slice(0, textarea.selectionEnd ?? 0);
    const marker = document.createElement('span');
    marker.textContent = '​';
    mirror.appendChild(marker);
    document.body.appendChild(mirror);
    const offset = marker.offsetTop;
    const height = marker.offsetHeight;
    mirror.remove();
    const top = textarea.getBoundingClientRect().top + (parseFloat(styles.borderTopWidth) || 0) + offset - textarea.scrollTop;
    return { top, bottom: top + height };
  }
}

/** The ancestors that scroll vertically, nearest first. */
function scrollingAncestors(el: HTMLElement): HTMLElement[] {
  const found: HTMLElement[] = [];
  for (let p = el.parentElement; p && p !== document.body && p !== document.documentElement; p = p.parentElement) {
    const overflow = getComputedStyle(p).overflowY;
    if ((overflow === 'auto' || overflow === 'scroll' || overflow === 'overlay') && p.scrollHeight > p.clientHeight) found.push(p);
  }
  return found;
}

/** What is on screen: the visual viewport (the part above the keyboard), clipped by each scrolling ancestor. */
function visibleArea(scrollers: HTMLElement[]): { top: number; bottom: number } {
  const vv = window.visualViewport;
  let top = vv ? vv.offsetTop : 0;
  let bottom = vv ? vv.offsetTop + vv.height : window.innerHeight;
  for (const el of scrollers) {
    const rect = el.getBoundingClientRect();
    top = Math.max(top, rect.top);
    bottom = Math.min(bottom, rect.bottom);
  }
  return { top, bottom };
}
